import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, CheckCircle, XCircle, RotateCcw, ShieldCheck } from 'lucide-react';
import { InfoField, Avatar, Chevron, EmptyState, verificationStatusLabels, verificationStatusColors } from './shared';

export function VerificationsTab({ verifications, onAction, processingId }) {
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [comment, setComment] = useState('');

  const filtered = verifications.filter(v => {
    if (filter && v.verification_status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return [v.email, v.verification_data?.company_name, v.company?.name, v.verification_data?.vat_number].some(f => f?.toLowerCase().includes(q));
    }
    return true;
  });

  const handleAction = async (userId, action) => {
    await onAction(userId, action, comment);
    setComment('');
    setExpandedId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input placeholder="Rechercher par email, entreprise, TVA..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="verification-search-input" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['', 'pending', 'verified', 'rejected'].map(f => (
            <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)} className={filter === f ? 'bg-[#1F3F72] text-white' : ''} data-testid={`filter-${f || 'all'}`}>
              {f === '' ? 'Tous' : verificationStatusLabels[f]}
            </Button>
          ))}
        </div>
      </div>
      {filtered.length === 0 ? (
        <EmptyState icon={ShieldCheck} text="Aucune demande" />
      ) : (
        <div className="space-y-3">
          {filtered.map((v) => (
            <div key={v.id} className="bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700 overflow-hidden" data-testid={`verification-${v.id}`}>
              <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-750 transition-colors" onClick={() => setExpandedId(expandedId === v.id ? null : v.id)}>
                <div className="flex items-center gap-3 flex-1">
                  <Avatar name={`${v.first_name} ${v.last_name}`} status={v.verification_status} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-neutral-900 dark:text-white">{v.first_name} {v.last_name}</p>
                    <p className="text-xs text-neutral-500">{v.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-200">{v.verification_data?.company_name || v.company?.name || '-'}</p>
                    <p className="text-xs text-neutral-400">{v.verification_data?.vat_number || '-'}</p>
                  </div>
                  <Badge className={`text-[10px] ${verificationStatusColors[v.verification_status] || ''}`}>{verificationStatusLabels[v.verification_status]}</Badge>
                  <Chevron open={expandedId === v.id} />
                </div>
              </div>
              {expandedId === v.id && (
                <div className="border-t dark:border-neutral-700 px-4 py-5 bg-neutral-50/50 dark:bg-neutral-850">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5 text-sm">
                    <InfoField label="Entreprise" value={v.verification_data?.company_name || '-'} />
                    <InfoField label="Numero de TVA" value={v.verification_data?.vat_number || '-'} />
                    <InfoField label="Pays" value={v.verification_data?.country || '-'} />
                    <InfoField label="Adresse" value={v.verification_data?.company_address || '-'} />
                    <InfoField label="Email" value={v.email} />
                    <InfoField label="Date de demande" value={v.verification_data?.submitted_at ? new Date(v.verification_data.submitted_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'} />
                  </div>
                  {v.verification_data?.admin_comment && (
                    <div className="mb-4 p-3 bg-neutral-100 dark:bg-neutral-700 rounded-lg text-sm"><span className="text-neutral-500 font-medium">Commentaire precedent :</span><p className="text-neutral-700 dark:text-neutral-300 mt-0.5">{v.verification_data.admin_comment}</p></div>
                  )}
                  <div className="mb-4"><Input placeholder="Commentaire admin (optionnel)..." value={comment} onChange={(e) => setComment(e.target.value)} data-testid={`admin-comment-${v.id}`} /></div>
                  <div className="flex flex-wrap gap-2">
                    {v.verification_status !== 'verified' && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={(e) => { e.stopPropagation(); handleAction(v.id, 'approved'); }} disabled={processingId === v.id} data-testid={`approve-verification-${v.id}`}><CheckCircle className="w-4 h-4 mr-1" /> Approuver</Button>}
                    {v.verification_status !== 'rejected' && <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); handleAction(v.id, 'rejected'); }} disabled={processingId === v.id} data-testid={`reject-verification-${v.id}`}><XCircle className="w-4 h-4 mr-1" /> Refuser</Button>}
                    {v.verification_status !== 'pending' && <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleAction(v.id, 'pending'); }} disabled={processingId === v.id} data-testid={`pending-verification-${v.id}`}><RotateCcw className="w-4 h-4 mr-1" /> Remettre en attente</Button>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
